// ptou.ts
import type { ValveParameterInference } from './valveParameterInference';
import type { Datapoint, Unit as BaseUnit } from './types';

// デバイスタイプ (ptou: PTOU計, keyence: キーエンス)
export const SOURCE_TYPE = {
  PTOU: 'ptou',
  KEYENCE: 'keyence',
} as const;

export type SourceType = (typeof SOURCE_TYPE)[keyof typeof SOURCE_TYPE];

// 操作パネル スイッチ (A, B, C) 割り当てモード
export const PANEL_MODE = {
  DISABLED: 0,
  TANK_FIXED: 1, // 「貯水」スイッチ専用（A/B/Cでは選択不可）
  TANK_HIGH: 2,
  TANK_LOW: 3,
  MOBILE_SINK_1: 4,
  MOBILE_SINK_2: 5,
  MOBILE_SINK_3: 6,
  HOSE_WASH: 7,
  CONTINUOUS: 8,
} as const;

export type PanelMode = (typeof PANEL_MODE)[keyof typeof PANEL_MODE];

export const PANEL_MODE_LABEL: Record<PanelMode, string> = {
  [PANEL_MODE.DISABLED]: '無効',
  [PANEL_MODE.TANK_FIXED]: '貯水',
  [PANEL_MODE.TANK_HIGH]: '貯水高',
  [PANEL_MODE.TANK_LOW]: '貯水低',
  [PANEL_MODE.MOBILE_SINK_1]: 'モービルシンク1',
  [PANEL_MODE.MOBILE_SINK_2]: 'モービルシンク2',
  [PANEL_MODE.MOBILE_SINK_3]: 'モービルシンク3',
  [PANEL_MODE.HOSE_WASH]: 'ホース洗浄',
  [PANEL_MODE.CONTINUOUS]: '連続',
} as const;

export const getPanelModeLabel = (mode: number): string => {
  return PANEL_MODE_LABEL[mode as PanelMode] ?? '未設定';
};

export const PANEL_MODE_SELECT_OPTIONS = Object.entries(PANEL_MODE_LABEL)
  .filter(([key]) => Number(key) !== PANEL_MODE.TANK_FIXED)
  .map(([key, label]) => ({ value: Number(key), label }));

export type Device = {
  serialNumber: string;
  readonly deviceId: string;
  measurementLogSendInterval: 15 | 30 | 60;
  valveLogSendInterval: 15 | 30 | 60 | 1440;
  systemLogSendInterval: 60 | 1440;
  errorLogSendInterval: 60 | 1440;
  measurementLogSendEnabled: boolean;
  valveLogSendEnabled: boolean;
  systemLogSendEnabled: boolean;
  errorLogSendEnabled: boolean;
};

// --- 洗浄モードパラメータ ---
export type WashingParams = {
  intermittentOpenHigh: number;
  intermittentDurationHigh: number;
  intermittentOpenLow: number;
  intermittentDurationLow: number;
  intermittentDurationTransition: number;
  intermittentDurationMax: number;
};

// --- 貯水モードパラメータ ---
export type StorageParams = {
  tankOpenDefault: number;
  tankDurationDefault: number;
};

export type StorageSubPanelParams = {
  tankOpenModeA: number;
  tankOpenModeB: number;
  tankOpenModeC: number;
  tankDurationModeA: number;
  tankDurationModeB: number;
  tankDurationModeC: number;
};

export type StorageSubPanelParamsWithMode = StorageSubPanelParams & {
  panelModeA: PanelMode;
  panelModeB: PanelMode;
  panelModeC: PanelMode;
};

// --- 手洗いモードパラメータ ---
// NOTE: sensorErrorDuration が他と意味が被るなら名前を分ける（例：handWashSensorErrorDuration）
export type HandWashParams = {
  handWashOpen: number;
  handWashDuration: number;
  handWashSensorDuration: number;
  sensorErrorDuration: number;
};

export type StopParams = {
  stopOperation: boolean;
};

export type DemandControlParams = {
  controlCycle: 15 | 30;
  controlEnabled: boolean;
  dailyTargetSun: number;
  dailyTargetMon: number;
  dailyTargetTue: number;
  dailyTargetWed: number;
  dailyTargetThu: number;
  dailyTargetFri: number;
  dailyTargetSat: number;
  controlMaxOperationCount: number;
  controlAddDurationLow: number;
  controlAddOpenHi: number;
  controlDailyTargetThreshold: number;
};

export type Channel = WashingParams &
  StorageParams &
  StorageSubPanelParams &
  HandWashParams &
  StopParams &
  DemandControlParams & {
    id: number;
    unitId: BaseUnit['id'];
    no: 1 | 2 | 3 | 4;
    name: string;
    enabled: boolean;

    // デバイス情報
    sensorErrorDuration: number;
    panelModeA: PanelMode;
    panelModeB: PanelMode;
    panelModeC: PanelMode;

    datapoints: Datapoint[];
    // 最新バルブ判定アルゴリズム結果（無い場合はnull）
    latestValveParameterInference: ValveParameterInference | null;
    createdAt: string;
    updatedAt: string;
    /** 系統設定 (バルブ/洗浄/AI 等) のデバイスへの最終書き込み時刻。null は未同期 */
    lastSyncedAt: string | null;
  };

export type UnitChannel = Pick<Channel, 'id' | 'name' | 'enabled' | 'no' | 'lastSyncedAt'>;
